import React, { useCallback, useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { toast } from "react-toastify";
import { palette } from "./theme";

// The global OCR switch: one backend document that sends EVERY org to Extend
// within a minute, no deploy. Backend: GET/POST /admin/organizations/ocr-routing/global.
// Flipping it on is a two-click action so a stray click cannot move all traffic.

const sx = {
  panel: (armed) => ({
    background: palette.surface,
    border: `1px solid ${armed ? palette.danger : palette.border}`,
    borderRadius: 16,
    padding: "20px 22px",
    marginBottom: 22,
    boxShadow: palette.shadow,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    flexWrap: "wrap",
  }),
  text: { minWidth: 0, flex: "1 1 420px" },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: palette.textDim,
    marginBottom: 6,
  },
  dot: (color) => ({ width: 8, height: 8, borderRadius: 999, background: color, display: "inline-block" }),
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: palette.text },
  help: { margin: "6px 0 0", fontSize: 13, color: palette.textMuted, maxWidth: 720, lineHeight: 1.5 },
  status: (on) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 10,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: on ? palette.danger : palette.ok,
    background: on ? palette.dangerSoft : palette.okSoft,
  }),
  meta: { fontSize: 12, color: palette.textDim, marginTop: 8 },
  actions: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  button: (kind, enabled) => ({
    padding: "10px 16px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.55,
    border: `1px solid ${kind === "danger" ? palette.danger : kind === "ok" ? palette.ok : palette.borderStrong}`,
    background: kind === "danger" ? palette.danger : kind === "ok" ? palette.ok : palette.surface,
    color: kind === "ghost" ? palette.text : "#fff",
  }),
  note: {
    padding: "9px 12px",
    borderRadius: 10,
    border: `1px solid ${palette.borderStrong}`,
    fontSize: 13,
    color: palette.text,
    minWidth: 240,
  },
};

function formatWhen(ts) {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString();
}

export default function GlobalOcrSwitchPanel({ env, backends }) {
  const [state, setState] = useState(null); // { everyoneToExtend, updatedAt, updatedBy, note }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [armed, setArmed] = useState(false);
  const [note, setNote] = useState("");

  // The page builds the backend list on every render; key on its contents so
  // the fetch runs once per environment, not once per render.
  const backendsKey = (backends || []).join("|");
  const request = useCallback(
    async (method, body) => {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId) {
        throw new Error("Admin user not found. Please refresh and sign in again.");
      }
      let lastError = "Could not reach the backend";
      for (const baseUrl of backendsKey.split("|").filter(Boolean)) {
        try {
          const url = new URL(`${baseUrl}/admin/organizations/ocr-routing/global`);
          if (method === "GET") url.searchParams.set("userId", userId);
          const resp = await fetch(url.toString(), {
            method,
            headers: { "Content-Type": "application/json" },
            body: method === "GET" ? undefined : JSON.stringify({ userId, ...body }),
          });
          const json = await resp.json().catch(() => ({}));
          if (!resp.ok || !json.success) {
            lastError = json.reason || `Request failed on ${baseUrl}`;
            continue;
          }

          return json.global;
        } catch (err) {
          lastError = err?.message || `Could not reach ${baseUrl}`;
        }
      }
      throw new Error(lastError);
    },
    [backendsKey],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request("GET")
      .then((global) => {
        if (!cancelled) setState(global);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [request, env]);

  const flip = async (everyoneToExtend) => {
    setSaving(true);
    try {
      const global = await request("POST", { everyoneToExtend, note });
      setState(global);
      setArmed(false);
      setNote("");
      toast.success(
        everyoneToExtend
          ? "Every org now goes to Extend. Takes effect within a minute."
          : "Switch off. Per-org engine settings apply again within a minute.",
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const on = state?.everyoneToExtend === true;
  const envLabel = env === "dev" ? "Development" : "Production";

  return (
    <section style={sx.panel(armed)}>
      <div style={sx.text}>
        <div style={sx.eyebrow}>
          <span style={sx.dot(on ? palette.danger : palette.ok)} />
          Addy Intelligence · {envLabel}
        </div>
        <h2 style={sx.title}>Send every org to Extend</h2>
        <p style={sx.help}>
          The emergency switch for document reading. On, every organization uses Extend, whatever
          its own engine setting says, within about a minute and without a deploy. Off, the per-org
          engine settings on this page apply again.
        </p>
        <div style={sx.status(on)}>
          <span style={sx.dot(on ? palette.danger : palette.ok)} />
          {loading ? "Checking…" : on ? "ON: all orgs on Extend" : "Off: per-org settings apply"}
        </div>
        {state?.updatedAt && (
          <div style={sx.meta}>
            Last changed {formatWhen(state.updatedAt)}
            {state.updatedBy ? ` by ${state.updatedBy}` : ""}
            {state.note ? ` · "${state.note}"` : ""}
          </div>
        )}
      </div>
      <div style={sx.actions}>
        {on ? (
          <button
            type="button"
            style={sx.button("ok", !saving && !loading)}
            disabled={saving || loading}
            onClick={() => flip(false)}
          >
            {saving ? "Saving…" : "Turn off, back to per-org settings"}
          </button>
        ) : armed ? (
          <>
            <input
              type="text"
              placeholder="Why? (optional, shown here)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={sx.note}
            />
            <button
              type="button"
              style={sx.button("danger", !saving)}
              disabled={saving}
              onClick={() => flip(true)}
            >
              {saving ? "Saving…" : "Confirm: every org to Extend"}
            </button>
            <button type="button" style={sx.button("ghost", !saving)} disabled={saving} onClick={() => setArmed(false)}>
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            style={sx.button("danger", !loading)}
            disabled={loading}
            onClick={() => setArmed(true)}
          >
            Send every org to Extend…
          </button>
        )}
      </div>
    </section>
  );
}
